/**
 * CRUD Test Runner
 *
 * End-to-end tests for backend REST API data endpoints.
 * Tests the full lifecycle (Create → Read → Update → Read → Delete → Verify)
 * for each entity type using the /cloud_data/* endpoints.
 *
 * Uses the user's current authenticated session. All data is encrypted
 * client-side before sending, matching the real app flow.
 */

import type {
  CloudTask,
  CloudProject,
  CloudNote,
  CloudContact,
  CloudLocation,
} from '../types'
import {
  listCloudTasks,
  getCloudTask,
  upsertCloudTask,
  deleteCloudTask,
  listCloudProjects,
  upsertCloudProject,
  deleteCloudProject,
  listCloudNotes,
  getCloudNote,
  upsertCloudNote,
  deleteCloudNote,
  listCloudContacts,
  listCloudLocations,
} from './api'

// ── Types ──────────────────────────────────────────────────────

export interface CrudTestStep {
  label: string
  action: () => Promise<unknown>
  validate: (result: unknown) => string | null // null = pass
}

export interface CrudTestResult {
  name: string
  passed: boolean
  steps: {
    label: string
    passed: boolean
    error?: string
    detail?: string
    duration: number
  }[]
  duration: number
  error?: string
}

export type CrudTestProgressCallback = (
  setName: string,
  stepIndex: number,
  totalSteps: number,
  status: 'running' | 'passed' | 'failed',
  detail?: string,
) => void

export interface CrudTestSet {
  name: string
  description: string
  buildSteps: () => CrudTestStep[]
}

// ── Helpers ────────────────────────────────────────────────────

function uid(): string {
  return `crud_test_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function nowIso(): string {
  return new Date().toISOString()
}

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ── Test Set: Tasks ────────────────────────────────────────────

function taskCrudTests(): CrudTestSet {
  return {
    name: 'Task CRUD',
    description: 'Create, read, update, delete a task via /cloud_data/tasks',
    buildSteps() {
      const id = uid()
      let created: CloudTask | null = null

      return [
        {
          label: 'Create task (PUT)',
          async action() {
            const task: CloudTask = {
              id,
              title: `CRUD Test Task ${id}`,
              description: 'Created by CRUD test runner',
              dueDate: todayStr(),
              priority: 2,
              status: 0,
              type: 0,
              isAllDay: false,
              tags: ['crud-test'],
              createdAt: nowIso(),
              lastUpdatedAt: nowIso(),
            }
            await upsertCloudTask(task)
            return task
          },
          validate(result) {
            return result ? null : 'upsertCloudTask returned no result'
          },
        },
        {
          label: 'Read task (GET by ID)',
          async action() {
            created = await getCloudTask(id)
            return created
          },
          validate(result) {
            const t = result as CloudTask | null
            if (!t) return `Task ${id} not found after create`
            if (t.title !== `CRUD Test Task ${id}`) return `Title mismatch: "${t.title}"`
            return null
          },
        },
        {
          label: 'List tasks (GET all) — verify present',
          async action() {
            return await listCloudTasks()
          },
          validate(result) {
            const tasks = result as CloudTask[]
            const found = tasks.find(t => t.id === id)
            return found ? null : `Task ${id} not found in list (${tasks.length} tasks)`
          },
        },
        {
          label: 'Update task (PUT)',
          async action() {
            const updated: CloudTask = {
              ...(created || { id }),
              id,
              title: `CRUD Test Task ${id} UPDATED`,
              description: 'Updated by CRUD test runner',
              priority: 0,
              status: 1,
              lastUpdatedAt: nowIso(),
            }
            await upsertCloudTask(updated)
            return updated
          },
          validate() { return null },
        },
        {
          label: 'Read updated task — verify changes',
          async action() {
            return await getCloudTask(id)
          },
          validate(result) {
            const t = result as CloudTask | null
            if (!t) return 'Task not found after update'
            if (!t.title?.includes('UPDATED')) return `Title not updated: "${t.title}"`
            if (t.priority !== 0) return `Priority not updated: ${t.priority}`
            return null
          },
        },
        {
          label: 'Delete task (DELETE)',
          async action() {
            await deleteCloudTask(id)
            return true
          },
          validate() { return null },
        },
        {
          label: 'Verify deletion — GET should return null',
          async action() {
            return await getCloudTask(id)
          },
          validate(result) {
            return result === null ? null : 'Task still exists after deletion'
          },
        },
      ]
    },
  }
}

// ── Test Set: Projects ─────────────────────────────────────────

function projectCrudTests(): CrudTestSet {
  return {
    name: 'Project CRUD',
    description: 'Create, read, update, delete a project via /cloud_data/projects',
    buildSteps() {
      const id = uid()

      return [
        {
          label: 'Create project (PUT)',
          async action() {
            const project: CloudProject = {
              id,
              name: `CRUD Test Project ${id}`,
              description: 'Created by CRUD test runner',
              color_index: 3,
              created_at: nowIso(),
              updated_at: nowIso(),
            }
            await upsertCloudProject(project)
            return project
          },
          validate() { return null },
        },
        {
          label: 'List projects — verify present',
          async action() {
            return await listCloudProjects()
          },
          validate(result) {
            const projects = result as CloudProject[]
            const found = projects.find(p => p.id === id)
            return found ? null : `Project ${id} not found in list (${projects.length} projects)`
          },
        },
        {
          label: 'Update project (PUT)',
          async action() {
            const updated: CloudProject = {
              id,
              name: `CRUD Test Project ${id} UPDATED`,
              description: 'Updated by CRUD test runner',
              color_index: 5,
              updated_at: nowIso(),
            }
            await upsertCloudProject(updated)
            return updated
          },
          validate() { return null },
        },
        {
          label: 'List projects — verify update',
          async action() {
            return await listCloudProjects()
          },
          validate(result) {
            const projects = result as CloudProject[]
            const found = projects.find(p => p.id === id)
            if (!found) return 'Project not found after update'
            if (!found.name?.includes('UPDATED')) return `Name not updated: "${found.name}"`
            return null
          },
        },
        {
          label: 'Delete project (DELETE)',
          async action() {
            await deleteCloudProject(id)
            return true
          },
          validate() { return null },
        },
        {
          label: 'Verify deletion',
          async action() {
            const all = await listCloudProjects()
            return all.find(p => p.id === id) || null
          },
          validate(result) {
            return result === null || result === undefined ? null : 'Project still in list after deletion'
          },
        },
      ]
    },
  }
}

// ── Test Set: Notes ────────────────────────────────────────────

function noteCrudTests(): CrudTestSet {
  return {
    name: 'Note CRUD',
    description: 'Create, read, update, delete a note via /cloud_data/notes',
    buildSteps() {
      const id = uid()
      let created: CloudNote | null = null

      return [
        {
          label: 'Create note (PUT)',
          async action() {
            const note: CloudNote = {
              id,
              title: `CRUD Test Note ${id}`,
              content: 'Created by CRUD test runner',
              type: 0,
              tags: ['crud-test'],
              created_at: nowIso(),
              last_updated_at: nowIso(),
            }
            await upsertCloudNote(note)
            return note
          },
          validate() { return null },
        },
        {
          label: 'Read note (GET by ID)',
          async action() {
            created = await getCloudNote(id)
            return created
          },
          validate(result) {
            const n = result as CloudNote | null
            if (!n) return `Note ${id} not found after create`
            if (n.title !== `CRUD Test Note ${id}`) return `Title mismatch: "${n.title}"`
            return null
          },
        },
        {
          label: 'List notes — verify present',
          async action() {
            return await listCloudNotes()
          },
          validate(result) {
            const notes = result as CloudNote[]
            const found = notes.find(n => n.id === id)
            return found ? null : `Note ${id} not found in list (${notes.length} notes)`
          },
        },
        {
          label: 'Update note (PUT)',
          async action() {
            const updated: CloudNote = {
              ...(created || { id }),
              id,
              title: `CRUD Test Note ${id} UPDATED`,
              content: 'Updated by CRUD test runner',
              last_updated_at: nowIso(),
            }
            await upsertCloudNote(updated)
            return updated
          },
          validate() { return null },
        },
        {
          label: 'Read updated note — verify changes',
          async action() {
            return await getCloudNote(id)
          },
          validate(result) {
            const n = result as CloudNote | null
            if (!n) return 'Note not found after update'
            if (!n.title?.includes('UPDATED')) return `Title not updated: "${n.title}"`
            if (n.content !== 'Updated by CRUD test runner') return `Content not updated: "${n.content}"`
            return null
          },
        },
        {
          label: 'Delete note (DELETE)',
          async action() {
            await deleteCloudNote(id)
            return true
          },
          validate() { return null },
        },
        {
          label: 'Verify deletion — GET should return null',
          async action() {
            return await getCloudNote(id)
          },
          validate(result) {
            return result === null ? null : 'Note still exists after deletion'
          },
        },
      ]
    },
  }
}

// ── Test Set: Contacts (Read-only + Delete) ────────────────────

function contactCrudTests(): CrudTestSet {
  return {
    name: 'Contact Read/List',
    description: 'List contacts via /cloud_data/contacts (no create endpoint)',
    buildSteps() {
      return [
        {
          label: 'List contacts (GET)',
          async action() {
            return await listCloudContacts()
          },
          validate(result) {
            const contacts = result as CloudContact[]
            // Pass even if empty — just verify the endpoint works
            return Array.isArray(contacts) ? null : 'Expected array response'
          },
        },
      ]
    },
  }
}

// ── Test Set: Locations (Read-only) ────────────────────────────

function locationCrudTests(): CrudTestSet {
  return {
    name: 'Location Read/List',
    description: 'List locations via /cloud_data/locations (no create endpoint)',
    buildSteps() {
      return [
        {
          label: 'List locations (GET)',
          async action() {
            return await listCloudLocations()
          },
          validate(result) {
            const locs = result as CloudLocation[]
            return Array.isArray(locs) ? null : 'Expected array response'
          },
        },
      ]
    },
  }
}

// ── Test Set: Delta Sync ───────────────────────────────────────

function deltaSyncTests(): CrudTestSet {
  return {
    name: 'Delta Sync',
    description: 'Tests since-based delta sync for tasks, notes, and projects',
    buildSteps() {
      const id = uid()
      const beforeCreate = nowIso()

      return [
        {
          label: 'Create task for delta test',
          async action() {
            const task: CloudTask = {
              id,
              title: `Delta Sync Test ${id}`,
              description: 'Delta sync test',
              priority: 1,
              status: 0,
              type: 0,
              createdAt: nowIso(),
              lastUpdatedAt: nowIso(),
            }
            await upsertCloudTask(task)
            return task
          },
          validate() { return null },
        },
        {
          label: 'List tasks with since — should include new task',
          async action() {
            return await listCloudTasks(beforeCreate)
          },
          validate(result) {
            const tasks = result as CloudTask[]
            const found = tasks.find(t => t.id === id)
            return found ? null : `Delta task not found in since-filtered list (${tasks.length} results)`
          },
        },
        {
          label: 'List tasks with future since — should not include task',
          async action() {
            // Use a timestamp 1 hour in the future
            const future = new Date(Date.now() + 3600000).toISOString()
            return { tasks: await listCloudTasks(future), futureTs: future }
          },
          validate(result) {
            const { tasks } = result as { tasks: CloudTask[] }
            const found = tasks.find(t => t.id === id)
            // Pass either way — the backend might not support strict future filtering
            if (found) {
              console.log('  [info] Task found even with future since — backend may not filter strictly')
            }
            return null
          },
        },
        {
          label: 'Cleanup — delete test task',
          async action() {
            await deleteCloudTask(id)
            return true
          },
          validate() { return null },
        },
      ]
    },
  }
}

// ── Test Set: Encryption roundtrip ─────────────────────────────

function encryptionRoundtripTests(): CrudTestSet {
  return {
    name: 'Encryption Roundtrip',
    description: 'Verifies data is encrypted at rest and decrypted on read',
    buildSteps() {
      const id = uid()
      const specialContent = 'Unicode test: Hello\u2603\uD83C\uDF1F \u00E4\u00F6\u00FC\u00DF & "quotes" <tags> \'{apostrophes}\''

      return [
        {
          label: 'Create note with special characters',
          async action() {
            const note: CloudNote = {
              id,
              title: `Encryption Test ${id}`,
              content: specialContent,
              type: 0,
              created_at: nowIso(),
              last_updated_at: nowIso(),
            }
            await upsertCloudNote(note)
            return note
          },
          validate() { return null },
        },
        {
          label: 'Read back — verify content preserved through encrypt/decrypt',
          async action() {
            return await getCloudNote(id)
          },
          validate(result) {
            const n = result as CloudNote | null
            if (!n) return 'Note not found'
            if (n.content !== specialContent) return `Content mismatch after encrypt/decrypt roundtrip:\nExpected: ${specialContent}\nGot: ${n.content}`
            return null
          },
        },
        {
          label: 'Cleanup — delete test note',
          async action() {
            await deleteCloudNote(id)
            return true
          },
          validate() { return null },
        },
      ]
    },
  }
}

// ── Registry ───────────────────────────────────────────────────

const CRUD_TEST_SETS: CrudTestSet[] = [
  taskCrudTests(),
  projectCrudTests(),
  noteCrudTests(),
  contactCrudTests(),
  locationCrudTests(),
  deltaSyncTests(),
  encryptionRoundtripTests(),
]

export function getCrudTestSets(): { name: string; description: string }[] {
  return CRUD_TEST_SETS.map(s => ({ name: s.name, description: s.description }))
}

// ── Execution ──────────────────────────────────────────────────

async function runCrudSteps(
  setName: string,
  steps: CrudTestStep[],
  onProgress?: CrudTestProgressCallback,
): Promise<{ stepResults: CrudTestResult['steps']; allPassed: boolean }> {
  const stepResults: CrudTestResult['steps'] = []
  let allPassed = true

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepStart = performance.now()
    onProgress?.(setName, i, steps.length, 'running', step.label)

    console.log(`\n--- [${setName}] Step ${i + 1}/${steps.length}: ${step.label} ---`)

    try {
      const result = await step.action()
      const duration = performance.now() - stepStart

      // Build detail string for logging
      let detail = ''
      if (result && typeof result === 'object' && 'id' in (result as Record<string, unknown>)) {
        detail = `id=${(result as Record<string, unknown>).id}`
      } else if (Array.isArray(result)) {
        detail = `${result.length} items`
      } else if (result === null) {
        detail = 'null'
      } else if (result === true) {
        detail = 'ok'
      }

      console.log(`  Result (${(duration / 1000).toFixed(2)}s): ${detail}`)

      const error = step.validate(result)
      const stepPassed = !error
      if (!stepPassed) allPassed = false

      console.log(`  ${stepPassed ? 'PASS' : `FAIL — ${error}`}`)

      stepResults.push({
        label: step.label,
        passed: stepPassed,
        error: error || undefined,
        detail,
        duration,
      })

      onProgress?.(setName, i, steps.length, stepPassed ? 'passed' : 'failed', error || undefined)
    } catch (e) {
      const duration = performance.now() - stepStart
      const error = `Exception: ${e}`
      console.error(`  FAIL — ${error}`)
      allPassed = false

      stepResults.push({
        label: step.label,
        passed: false,
        error,
        duration,
      })

      onProgress?.(setName, i, steps.length, 'failed', error)
    }
  }

  return { stepResults, allPassed }
}

/**
 * Run a single CRUD test set.
 */
export async function runCrudTestSet(
  setName: string,
  onProgress?: CrudTestProgressCallback,
): Promise<CrudTestResult> {
  const testSet = CRUD_TEST_SETS.find(s => s.name === setName)
  if (!testSet) {
    return { name: setName, passed: false, steps: [], duration: 0, error: 'Test set not found' }
  }

  const steps = testSet.buildSteps()
  const totalStart = performance.now()

  console.log(`\n${'='.repeat(60)}`)
  console.log(`CRUD TEST: ${setName}`)
  console.log(`${'='.repeat(60)}`)

  const { stepResults, allPassed } = await runCrudSteps(setName, steps, onProgress)

  const totalDuration = performance.now() - totalStart
  console.log(`\n${setName}: ${allPassed ? 'ALL PASSED' : 'SOME FAILED'} (${(totalDuration / 1000).toFixed(1)}s)`)
  console.log('='.repeat(60))

  return { name: setName, passed: allPassed, steps: stepResults, duration: totalDuration }
}

/**
 * Run multiple CRUD test sets.
 */
export async function runAllCrudTests(
  selectedSets: string[],
  onProgress?: CrudTestProgressCallback,
): Promise<CrudTestResult[]> {
  const totalStart = performance.now()

  console.log(`\n${'='.repeat(60)}`)
  console.log(`CRUD TEST RUN: ${selectedSets.length} set(s)`)
  console.log(`${'='.repeat(60)}`)

  const results: CrudTestResult[] = []

  for (const setName of selectedSets) {
    const testSet = CRUD_TEST_SETS.find(s => s.name === setName)
    if (!testSet) {
      results.push({ name: setName, passed: false, steps: [], duration: 0, error: 'Test set not found' })
      continue
    }

    const steps = testSet.buildSteps()
    const setStart = performance.now()

    console.log(`\n${'─'.repeat(40)}`)
    console.log(`SET: ${setName}`)
    console.log(`${'─'.repeat(40)}`)

    const { stepResults, allPassed } = await runCrudSteps(setName, steps, onProgress)
    const setDuration = performance.now() - setStart

    console.log(`\n${setName}: ${allPassed ? 'ALL PASSED' : 'SOME FAILED'} (${(setDuration / 1000).toFixed(1)}s)`)
    results.push({ name: setName, passed: allPassed, steps: stepResults, duration: setDuration })
  }

  const totalDuration = performance.now() - totalStart
  const passed = results.filter(r => r.passed).length
  console.log(`\n${'='.repeat(60)}`)
  console.log(`ALL DONE: ${passed}/${results.length} sets passed (${(totalDuration / 1000).toFixed(1)}s total)`)
  console.log('='.repeat(60))

  return results
}
